import { authReducer, initialState } from "@/contexts/authReducer";
import { fetchAccount } from "@/services/account.queries";
import { updateAppUser } from "@/services/appuser.queries";
import { createAppUser } from "@/services/auth.queries";
import { useToastNotify } from "@/stores/toastStore";
import type { AuthContextType, PendingEmail } from "@/types/auth.types";
import { SetNewPasswordError } from "@/utils/SetNewPasswordError";
import { validateToken } from "@/utils/auth/getPublicKey";
import { getPhoneDigits } from "@/utils/formatPhoneNumber";
import { getValueFromStorage, removeWhenLogout, saveToStorage } from "@/utils/secureStore";
import {
  type AuthenticationResultType,
  ChangePasswordCommand,
  CognitoIdentityProviderClient,
  ConfirmForgotPasswordCommand,
  ConfirmSignUpCommand,
  ForgotPasswordCommand,
  InitiateAuthCommand,
  ResendConfirmationCodeCommand,
  RespondToAuthChallengeCommand,
  SignUpCommand,
  UpdateUserAttributesCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import { useFocusEffect } from "@react-navigation/native";
import * as Sentry from "@sentry/react-native";
import { useT } from "@transifex/react";
import { useRouter } from "expo-router";
import { AccountFailReason, ReceiptDeliveryPreference } from "ipctg-inflow-types/InFlowEnums";
import type { ParkAccountPOSTSuccessResponse } from "ipctg-inflow-types/InFlowParkAPI";
import { type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from "react";
import { Platform } from "react-native";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Create a stable client instance outside the component
const client = new CognitoIdentityProviderClient({
  region: process.env.EXPO_PUBLIC_AWS_COGNITO_REGION,
});

const ACCOUNT_CHECK_INTERVAL = 5 * 60 * 1000;

function getMessage(err: any, fallbackMessage: string) {
  return typeof err === "string" ? err : fallbackMessage;
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const { notify } = useToastNotify();
  const lastAccountCheckRef = useRef<number>(0);
  const isInitializingRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const assertionPromiseRef = useRef<Promise<ParkAccountPOSTSuccessResponse> | null>(null);
  const router = useRouter();
  const t = useT();

  const handleError = useCallback(
    (err: unknown, fallbackMessage: string) => {
      console.error(t("Auth error:"), err instanceof Error ? err.message : fallbackMessage);
      notify({ severity: "error", message: fallbackMessage });
      dispatch({
        type: "SET_ERROR",
        payload: err instanceof Error ? err.message : getMessage(err, fallbackMessage),
      });
    },
    [notify, t],
  );

  const createUser = useCallback(async (cognitoIdToken: string, accessToken: string): Promise<void> => {
    try {
      const validatedToken = await validateToken(accessToken);

      await createAppUser({
        cognitoIDToken: cognitoIdToken,
        email: validatedToken.email,
        first: validatedToken.given_name ?? "",
        last: validatedToken.family_name ?? "",
        phone: validatedToken.phone_number ? `+${getPhoneDigits(validatedToken.phone_number)}` : "",
      });
    } catch (error: unknown) {
      console.error("Error trying to create account: ", error);
      Sentry.captureException(error, {
        extra: {
          data: {
            cognitoIdToken,
            accessToken,
          },
          localStorage: Platform.OS === "web" ? window.localStorage : null,
        },
      });
      throw error;
    }
  }, []);

  const signOut = useCallback(async () => {
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      dispatch({ type: "SIGN_OUT" });
      router.replace("/welcome");
      await removeWhenLogout();
      dispatch({ type: "SIGN_OUT" });
    } catch (error) {
      console.error("Error during sign out:", error);
      handleError(error, t("Error during logout"));
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [handleError, router, t]);

  const assertAccount = useCallback(
    async (cognitoIdToken: string, accessToken: string): Promise<ParkAccountPOSTSuccessResponse> => {
      const now = Date.now();

      // Check if we've done a recent account assertion and have valid user data
      if (now - lastAccountCheckRef.current < ACCOUNT_CHECK_INTERVAL && state.user) {
        return {
          first: state.user.first,
          last: state.user.last,
          email: state.user.email,
          phone: state.user.phone ? `+${getPhoneDigits(state.user.phone)}` : "",
          savedPlates: [],
          receiptDeliveryPreference: ReceiptDeliveryPreference.Email, // Use proper enum value
        };
      }

      // If there's already an assertion in progress, wait for it
      if (assertionPromiseRef.current !== null) {
        return assertionPromiseRef.current;
      }

      try {
        // Store the promise to handle concurrent calls
        assertionPromiseRef.current = (async () => {
          try {
            const accountResponse = await fetchAccount({ cognitoIDToken: cognitoIdToken });
            if (!accountResponse) {
              throw new Error(t("Account fetch returned no data"));
            }
            lastAccountCheckRef.current = now;
            return accountResponse;
          } catch (error: any) {
            if ((error?.failReason as string).includes(AccountFailReason.UserNotFound)) {
              await createUser(cognitoIdToken, accessToken);
              lastAccountCheckRef.current = 0;
              return await fetchAccount({ cognitoIDToken: cognitoIdToken });
            }
            throw error;
          }
        })();

        const result = await assertionPromiseRef.current;
        return result;
      } catch (error: any) {
        console.error("Error during account assertion:", error);
        // if failreason us Unauthorized, sign out and display notify
        if ((error?.failReason as string).includes(AccountFailReason.Unauthorized)) {
          notify({
            severity: "error",
            message: t("Your session has expired. Please sign in again."),
          });
          signOut();
        }
        Sentry.captureException(error, {
          extra: {
            data: {
              cognitoIdToken,
              accessToken,
              update,
            },
            localStorage: Platform.OS === "web" ? window.localStorage : null,
          },
        });
        throw error;
      } finally {
        assertionPromiseRef.current = null;
      }
    },
    [createUser, state.user, signOut, notify, t],
  );

  const handleAccessToken = useCallback(
    async (accessToken: string, idToken: string, refreshToken: string) => {
      // Only skip if we're re-initializing after initial setup
      if (isInitializingRef.current && hasInitializedRef.current) {
        return;
      }

      try {
        // Validate token first to avoid unnecessary API calls if token is invalid
        const validatedToken = await validateToken(accessToken);
        if (!validatedToken) {
          throw new Error(t("Token validation failed"));
        }

        const accountResponse = await assertAccount(idToken, accessToken);
        const { first, last, email: accountEmail, phone } = accountResponse;

        dispatch({
          type: "SET_USER",
          payload: {
            id: accessToken,
            email: accountEmail,
            first,
            last,
            phone,
            idToken,
            accessToken,
            refreshToken,
            updatedAt: new Date().toISOString(),
          },
        });

        // Set last token check time since we just validated
        dispatch({ type: "UPDATE_TOKEN_CHECK" });
      } catch (error) {
        console.error("Error handling access token:", error);
        if (error instanceof Error && error.message.includes(t("Token validation failed"))) {
          notify({
            severity: "error",
            message: t("Your session has expired. Please sign in again."),
          });
          signOut();
        } else {
          handleError(error, t("An unknown error occurred."));
        }
      }
    },
    [assertAccount, handleError, notify, signOut, t],
  );

  const handleAuthResponse = useCallback(
    async (authenticationResult: AuthenticationResultType | undefined, email: string) => {
      if (!authenticationResult) {
        throw new Error(t("Unexpected response format."));
      }

      const { AccessToken, IdToken, RefreshToken } = authenticationResult;

      if (!IdToken) {
        throw new Error(t("ID Token is missing."));
      }

      const validatedToken = await validateToken(AccessToken as string);
      if (!validatedToken) {
        throw new Error(t("Token validation failed."));
      }

      await Promise.all([
        saveToStorage("idToken", IdToken),
        saveToStorage("accessToken", AccessToken as string),
        saveToStorage("refreshToken", RefreshToken as string),
      ]);

      const accountResponse = await assertAccount(IdToken, AccessToken as string);
      const { first, last, email: accountEmail, phone } = accountResponse;

      dispatch({
        type: "SET_USER",
        payload: {
          id: validatedToken.sub || "",
          email: accountEmail || email,
          first: first || "",
          last: last || "",
          phone: phone || undefined,
          idToken: IdToken,
          accessToken: AccessToken,
          refreshToken: RefreshToken,
          updatedAt: new Date().toISOString(),
        },
      });

      return IdToken;
    },
    [assertAccount, t],
  );

  const handleRefreshToken = useCallback(
    async (refreshToken: string) => {
      try {
        const refreshCommand = new InitiateAuthCommand({
          AuthFlow: "REFRESH_TOKEN_AUTH",
          ClientId: process.env.EXPO_PUBLIC_AWS_COGNITO_CLIENT_ID,
          AuthParameters: { REFRESH_TOKEN: refreshToken },
        });

        const refreshResponse = await client.send(refreshCommand);

        if (refreshResponse.AuthenticationResult) {
          const { AccessToken, IdToken } = refreshResponse.AuthenticationResult;

          if (!IdToken || !AccessToken) {
            console.error("ID Token or Access Token is missing from refresh response");
            throw new Error(t("ID Token or Access Token is missing."));
          }

          await handleAccessToken(AccessToken, IdToken, refreshToken);
        }
      } catch (error) {
        console.error("Token refresh failed:", error);
        handleError(error, t("Failed to refresh token."));
      }
    },
    [handleAccessToken, handleError, t],
  );

  const checkTokenValidity = useCallback(async () => {
    if (!state.user?.accessToken) return;

    try {
      const isTokenValid = await validateToken(state.user.accessToken);
      if (!isTokenValid) {
        console.error("Token expired");
        await signOut();
      } else {
        dispatch({ type: "UPDATE_TOKEN_CHECK" });
      }
    } catch (error) {
      console.error("Token validation failed:", error);
      await signOut();
    }
  }, [state.user?.accessToken, signOut]);

  // Use a ref to track if we've already set up the interval
  const intervalRef = useRef<NodeJS.Timeout>();
  const initialCheckDoneRef = useRef(false);

  // Handle periodic token checks
  useEffect(() => {
    // Skip if no user is logged in
    if (!state.user?.accessToken) {
      return;
    }

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Skip initial check since token was already validated during handleAccessToken
    if (!initialCheckDoneRef.current) {
      initialCheckDoneRef.current = true;
    }

    // Set up new interval - checking every 5 minutes
    intervalRef.current = setInterval(checkTokenValidity, 5 * 60 * 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [state.user?.accessToken, checkTokenValidity]);

  // Only use useFocusEffect for additional checks when the app regains focus
  useFocusEffect(
    useCallback(() => {
      // Skip focus check if we haven't done initial check or if no user
      if (!initialCheckDoneRef.current || !state.user?.accessToken) {
        return;
      }

      const lastCheck = state.lastTokenCheck ?? 0;
      const now = Date.now();
      // Increase minimum time between checks to 2 minutes
      const MIN_CHECK_INTERVAL = 2 * 60 * 1000; // 2 minutes in milliseconds

      if (now - lastCheck < MIN_CHECK_INTERVAL) {
        return;
      }
      checkTokenValidity();
    }, [checkTokenValidity, state.user?.accessToken, state.lastTokenCheck]),
  );

  const redirectToNewPassword = useCallback(
    (session: string, userAttributes = "{}") => {
      const jsonParams = JSON.parse(userAttributes);
      const params = {
        email: jsonParams.email,
        first: jsonParams.given_name,
        last: jsonParams.family_name,
        phone: jsonParams.phone_number,
        session,
      };
      router.replace(
        `/auth/new-password?${Object.entries(params)
          .map(([key, val]) => (val ? `${key}=${encodeURIComponent(val)}` : undefined))
          .filter((val) => val !== undefined)
          .join("&")}`,
      );
    },
    [router.replace],
  );

  const setNewPassword = useCallback(
    async (email: string, password: string, first: string, last: string, phone: string | null, originalPhone: string | undefined, session: string) => {
      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "SET_ERROR", payload: null });

      try {
        const userAttributes = {
          "userAttributes.family_name": last,
          "userAttributes.given_name": first,
          "userAttributes.updated_at": Math.floor(new Date().getTime() / 1000).toString(),
        } as Record<string, string>;
        if (!originalPhone) {
          userAttributes["userAttributes.phone_number"] = phone as string;
        }
        const command = new RespondToAuthChallengeCommand({
          ClientId: process.env.EXPO_PUBLIC_AWS_COGNITO_CLIENT_ID,
          ChallengeName: "NEW_PASSWORD_REQUIRED",
          ChallengeResponses: {
            NEW_PASSWORD: password,
            USERNAME: email,
            ...userAttributes,
          },
          Session: session,
        });
        const response = await client.send(command);
        const cognitoIDToken = await handleAuthResponse(response.AuthenticationResult, email);
        await updateAppUser({
          cognitoIDToken,
          email,
          first,
          last,
          phone: originalPhone ?? (phone as string),
          receiptDeliveryPreference: null,
        });
      } catch (err) {
        if (err instanceof Error) {
          console.error("Error setting new password:", err.name, err.message);
          const errorActions: Record<string, () => void> = {
            LimitExceededException: () => handleError(err, t("The account has been locked due to too many failed attempts. Please contact support.")),
            NotAuthorizedException: () => handleError(err, t("Expired session, try to sign-in again.")),
            TooManyRequestsException: () => handleError(err, t("Too many login attempts. Please wait and try again later.")),
          };
          (errorActions[err.name] || (() => handleError(err, t("An unexpected error occurred. Please try again."))))();
        } else {
          handleError(err, t("An unknown error occurred."));
        }

        Sentry.captureException(err, {
          extra: {
            data: {
              email,
              password: "**CONFIDENTIAL**",
              first,
              last,
              phone,
              originalPhone,
              session,
            },
            localStorage: Platform.OS === "web" ? window.localStorage : null,
          },
        });
        throw err;
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [handleAuthResponse, handleError, t],
  );

  const signIn = useCallback(
    async (email: string, password: string) => {
      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "SET_ERROR", payload: null });

      try {
        const command = new InitiateAuthCommand({
          AuthFlow: "USER_PASSWORD_AUTH",
          ClientId: process.env.EXPO_PUBLIC_AWS_COGNITO_CLIENT_ID,
          AuthParameters: { USERNAME: email, PASSWORD: password },
        });

        const response = await client.send(command);

        if (response.ChallengeName === "NEW_PASSWORD_REQUIRED") {
          // Migrated user has to change his/er password
          redirectToNewPassword(response.Session as string, response.ChallengeParameters?.userAttributes);
          throw new SetNewPasswordError(t);
        }

        await handleAuthResponse(response.AuthenticationResult, email);
      } catch (err) {
        if (err instanceof Error) {
          console.error("Sign in error:", err.name, err.message);
          const errorActions: Record<string, () => void> = {
            NotAuthorizedException: () => {
              handleError(err, t("Invalid username or password. Please try again."));
              // Throw the exception to be handled by the sigin page
              throw err;
            },
            UserNotConfirmedException: () => {
              dispatch({ type: "SET_PENDING_EMAIL", payload: { email, password, type: "sign-up" } });
              handleError(err, t("Your account has not been verified. Please check your email for the verification code."));
              // resendCode();
              // We need to throw the error to prevent the caller from proceeding with navigation
              throw err;
            },
            SetNewPasswordError: () => {
              // Prevents the sigin to reset to welcome
              throw err;
            },
            PasswordResetRequiredException: () => handleError(err, t("Your password must be reset. Please check your email for instructions.")),
            UserNotFoundException: () => handleError(err, t("This account does not exist. Please check your email or sign up.")),
            TooManyRequestsException: () => handleError(err, t("Too many login attempts. Please wait and try again later.")),
            LimitExceededException: () => handleError(err, t("The account has been locked due to too many failed attempts. Please contact support.")),
          };

          (errorActions[err.name] || (() => handleError(err, t("An unexpected error occurred. Please try again."))))();
        } else {
          handleError(err, t("An unknown error occurred."));
        }

        Sentry.captureException(err, {
          extra: {
            data: {
              email,
              password: "**CONFIDENTIAL**",
            },
            localStorage: Platform.OS === "web" ? window.localStorage : null,
          },
        });
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [handleAuthResponse, handleError, redirectToNewPassword, t],
  );

  const signUp = useCallback(
    async (email: string, password: string, first: string, last: string, phone: string | null) => {
      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "SET_ERROR", payload: null });

      try {
        const command = new SignUpCommand({
          ClientId: process.env.EXPO_PUBLIC_AWS_COGNITO_CLIENT_ID,
          Username: email,
          Password: password,
          UserAttributes: [
            { Name: "email", Value: email },
            { Name: "given_name", Value: first },
            { Name: "family_name", Value: last },
            { Name: "updated_at", Value: Math.floor(new Date().getTime() / 1000).toString() },
            ...(phone ? [{ Name: "phone_number", Value: phone }] : []),
          ],
        });

        const response = await client.send(command);

        if (response.UserConfirmed === false && response.CodeDeliveryDetails) {
          dispatch({ type: "SET_PENDING_EMAIL", payload: { email, type: "sign-up" } });
          return;
        }
      } catch (err) {
        if (err instanceof Error) {
          if (err.name === "UsernameExistsException") {
            throw new Error(t("An account with this email already exists. Please sign in or reset your password."));
          }
          console.error("Sign up error:", err);
          throw new Error(t("Failed to sign up. Please try again."));
        }
        return;
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [t],
  );

  const confirmSignUp = useCallback(async (email: string, code: string) => {
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const command = new ConfirmSignUpCommand({
        ClientId: process.env.EXPO_PUBLIC_AWS_COGNITO_CLIENT_ID,
        Username: email,
        ConfirmationCode: code,
      });

      await client.send(command);
    } catch (err) {
      console.error("Confirmation error:", err);
      throw err;
    } finally {
      dispatch({ type: "SIGN_OUT" });
    }
  }, []);

  const forgotPassword = useCallback(async (email: string) => {
    dispatch({ type: "SET_LOADING", payload: true });
    dispatch({ type: "SET_PENDING_EMAIL", payload: { email, type: "reset-password" } });
    try {
      const command = new ForgotPasswordCommand({
        ClientId: process.env.EXPO_PUBLIC_AWS_COGNITO_CLIENT_ID,
        Username: email,
      });
      await client.send(command);
    } catch (err) {
      console.error("Password reset initiation error:", err);
      throw err;
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, []);

  const resetPassword = useCallback(async (email: string, code: string, newPassword: string) => {
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const command = new ConfirmForgotPasswordCommand({
        ClientId: process.env.EXPO_PUBLIC_AWS_COGNITO_CLIENT_ID,
        Username: email,
        ConfirmationCode: code,
        Password: newPassword,
      });

      await client.send(command);
    } catch (err) {
      console.error("Password reset error:", err);
      throw err;
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, []);

  const changePassword = useCallback(
    async (oldPassword: string, newPassword: string) => {
      if (!state.user?.accessToken) {
        console.error("Cannot change password: user not logged in");
        notify({
          severity: "error",
          message: t("User is not logged in."),
        });
        return;
      }

      dispatch({ type: "SET_LOADING", payload: true });
      try {
        const command = new ChangePasswordCommand({
          AccessToken: state.user.accessToken,
          PreviousPassword: oldPassword,
          ProposedPassword: newPassword,
        });

        await client.send(command);
      } catch (err) {
        console.error("Password change error:", err);
        handleError(err, t("Failed to change password. Please try again."));
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [handleError, state.user, notify, t],
  );

  const setPendingEmail = useCallback((payload: PendingEmail | null) => {
    dispatch({ type: "SET_PENDING_EMAIL", payload });
  }, []);

  const setError = useCallback((error: string | null) => {
    dispatch({ type: "SET_ERROR", payload: error });
  }, []);

  const resendCode = useCallback(async () => {
    if (!state.pendingEmail) {
      console.error("Cannot resend code: no pending email");
      notify({
        severity: "error",
        message: t("No email found to resend the code."),
      });
      return;
    }

    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const command = new ResendConfirmationCodeCommand({
        ClientId: process.env.EXPO_PUBLIC_AWS_COGNITO_CLIENT_ID,
        Username: state.pendingEmail.email,
      });

      await client.send(command);

      notify({
        severity: "success",
        message: t("Verification code has been resent successfully."),
      });
    } catch (err) {
      console.error("Resend code error:", err);
      Sentry.captureException(err, {
        extra: {
          localStorage: Platform.OS === "web" ? window.localStorage : null,
        },
      });

      if (err instanceof Error && err.name === "LimitExceededException") {
        handleError(err, t("Verification limit exceeded, please try again later."));
      } else {
        handleError(err, t("Failed to resend verification code."));
      }
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [state.pendingEmail, handleError, notify, t]);

  const initializeAuth = useCallback(async () => {
    if (isInitializingRef.current || hasInitializedRef.current) return;
    isInitializingRef.current = true;
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const [idToken, accessToken, refreshToken] = await Promise.all([
        getValueFromStorage("idToken"),
        getValueFromStorage("accessToken"),
        getValueFromStorage("refreshToken"),
      ]);

      if (accessToken && idToken && refreshToken) {
        await handleAccessToken(accessToken, idToken, refreshToken);
      }
    } catch (error) {
      console.error("Error initializing auth:", error);
      await removeWhenLogout();
    } finally {
      isInitializingRef.current = false;
      hasInitializedRef.current = true;
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [handleAccessToken]);

  const update = useCallback(
    async (email: string, first: string, last: string, phone: string | null) => {
      try {
        const command = new UpdateUserAttributesCommand({
          AccessToken: state.user?.accessToken,
          UserAttributes: [
            //{ Name: "email", Value: email }, // Currently not allowed
            { Name: "family_name", Value: last },
            { Name: "given_name", Value: first },
            { Name: "updated_at", Value: Math.floor(new Date().getTime() / 1000).toString() },
            ...(phone ? [{ Name: "phone_number", Value: `+${getPhoneDigits(phone)}` }] : []),
          ],
        });
        await client.send(command);
      } catch (err) {
        console.error("Account update error:", err);
        handleError(err, t("Failed to update account. Please try again."));
      }
    },
    [handleError, state.user?.accessToken, t],
  );

  // Add useEffect to initialize auth when the component mounts
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Add refresh interval effect
  useEffect(() => {
    if (!state.user?.refreshToken) return;

    const refreshInterval = setInterval(
      () => {
        if (state.user?.refreshToken) {
          handleRefreshToken(state.user?.refreshToken);
        }
      },
      45 * 60 * 1000,
    ); // Refresh every 45 minutes

    return () => clearInterval(refreshInterval);
  }, [state.user?.refreshToken, handleRefreshToken]);

  const authMemo = useMemo(
    () => ({
      changePassword,
      confirmSignUp,
      error: state.error,
      isLoading: state.isLoading,
      forgotPassword,
      pendingEmail: state.pendingEmail,
      resendCode,
      resetPassword,
      setError,
      setNewPassword,
      setPendingEmail,
      signIn,
      signOut,
      signUp,
      update,
      user: state.user,
    }),
    [
      changePassword,
      confirmSignUp,
      forgotPassword,
      resendCode,
      resetPassword,
      setError,
      setNewPassword,
      setPendingEmail,
      signIn,
      signOut,
      signUp,
      state.error,
      state.isLoading,
      state.pendingEmail,
      state.user,
      update,
    ],
  );

  return <AuthContext.Provider value={authMemo}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
